import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
    Box,
    Grid,
    Paper,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Snackbar,
    Alert,
    InputAdornment,
    IconButton,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SaveIcon from "@mui/icons-material/Save";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useAuth } from "../../../auth";
import { useEditUserPassword } from "../../../api/bpm/endpoints";

export const Route = createFileRoute("/__auth/profile/password")({
    component: ProfilePasswordComponent,
    staticData: {
        title: "routes.changePassword",
    },
});

function ProfilePasswordComponent() {
    const { t } = useTranslation();
    const { user, currentTenant } = useAuth();
    const navigate = useNavigate();

    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: "success" | "error";
    }>({ open: false, message: "", severity: "success" });

    const changePasswordMutation = useEditUserPassword();

    const isInvalid =
        currentPassword === "" ||
        newPassword === "" ||
        confirmPassword === "" ||
        newPassword !== confirmPassword;

    const handleSubmit = () => {
        if (!user?.id || isInvalid) return;

        changePasswordMutation.mutate(
            {
                tenant: currentTenant,
                userId: user.id,
                data: {
                    senha: newPassword,
                    confirmacaoSenha: confirmPassword,
                },
            },
            {
                onSuccess: () => {
                    setSnackbar({
                        open: true,
                        message: t("profile.password.success"),
                        severity: "success",
                    });
                    setCurrentPassword("");
                    setNewPassword("");
                    setConfirmPassword("");
                },
                onError: () => {
                    setSnackbar({
                        open: true,
                        message: t("profile.password.error"),
                        severity: "error",
                    });
                },
            }
        );
    };

    const handleCancel = () => {
        navigate({ to: "/" });
    };

    const visibilityToggle = (show: boolean, toggle: () => void) => (
        <InputAdornment position="end">
            <IconButton onClick={toggle} edge="end" size="small">
                {show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
            </IconButton>
        </InputAdornment>
    );

    return (
        <Box sx={{ maxWidth: 900, mx: "auto", py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mb: 3,
                }}
            >
                <Box
                    sx={{
                        width: { xs: 64, md: 80 },
                        height: { xs: 64, md: 80 },
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <LockOutlinedIcon sx={{ fontSize: { xs: 32, md: 40 }, color: "white" }} />
                </Box>
            </Box>

            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                {t("profile.password.title")}
            </Typography>

            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2, md: 3 },
                    mb: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                }}
            >
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <TextField
                            id="current-password"
                            label={t("profile.password.currentPassword")}
                            type={showCurrent ? "text" : "password"}
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            fullWidth
                            size="small"
                            autoFocus
                            variant="outlined"
                            InputProps={{
                                endAdornment: visibilityToggle(showCurrent, () => setShowCurrent(!showCurrent)),
                            }}
                        />
                    </Grid>

                    <Grid size={12}>
                        <TextField
                            id="new-password"
                            label={t("profile.password.newPassword")}
                            type={showNew ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            fullWidth
                            size="small"
                            variant="outlined"
                            InputProps={{
                                endAdornment: visibilityToggle(showNew, () => setShowNew(!showNew)),
                            }}
                        />
                    </Grid>

                    <Grid size={12}>
                        <TextField
                            id="confirm-password"
                            label={t("profile.password.confirmPassword")}
                            type={showConfirm ? "text" : "password"}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            fullWidth
                            size="small"
                            variant="outlined"
                            InputProps={{
                                endAdornment: visibilityToggle(showConfirm, () => setShowConfirm(!showConfirm)),
                            }}
                        />
                    </Grid>
                </Grid>
            </Paper>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
                <Button
                    id="password-cancel-btn"
                    variant="outlined"
                    onClick={handleCancel}
                >
                    {t("profile.password.cancel")}
                </Button>
                <Button
                    id="password-change-btn"
                    variant="contained"
                    startIcon={
                        changePasswordMutation.isPending ? (
                            <CircularProgress size={18} color="inherit" />
                        ) : (
                            <SaveIcon />
                        )
                    }
                    onClick={handleSubmit}
                    disabled={isInvalid || changePasswordMutation.isPending}
                >
                    {t("profile.password.changeButton")}
                </Button>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
